import React, { useEffect, useState } from "react";
import instance from "../../service/apiService";
import {
  Dialog,
  DialogSurface,
  Button,
  Checkbox,
  Divider,
  makeStyles,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  DismissRegular,
  ChevronRightRegular,
  ChevronDownRegular,
} from "@fluentui/react-icons";
import FileLinkImage from "../../assets/logo/file_link_logo.png";

interface AssystLinkModalProps {
  open: boolean;
  onClose: () => void;
  needsUpdate?: boolean;
  latestVersion?: string | null;
  platform?: string;
  installerName?: string;
  downloadUrl?: string;
}

const useStyles = makeStyles({
  surface: {
    width: "85vw",
    minWidth: "460px",
    maxWidth: "640px",
    height: "88%",
    padding: "1px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
});

const WIN_STEPS_X64 = [
  {
    num: 1,
    title: "Download AssystLinkApp-Setup-x64.exe",
    desc: "Click the button below to download AssystLinkApp-Setup-x64.exe. Save it to your Downloads folder or Desktop for easy access.",
  },
  {
    num: 2,
    title: "Install on your computer",
    desc: 'Run AssystLinkApp-Setup-x64.exe. The app installs silently and starts automatically in the system tray. When prompted, check "Create a Desktop Shortcut" for easy access.',
  },
  {
    num: 3,
    title: "Connect Assyst Link",
    desc: "Return here and click Browse — Assyst Link will connect automatically.",
  },
];

const MAC_STEPS = (dmgName: string) => [
  {
    num: 1,
    title: `Download ${dmgName}`,
    desc: `Click the button below to download ${dmgName}.`,
  },
  {
    num: 2,
    title: "Install on your Mac",
    desc: "Open the DMG file and drag AssystLinkApp to your Applications folder.",
  },
  {
    num: 3,
    title: "Launch AssystLinkApp",
    desc: "Launch AssystLinkApp from Applications — it will appear in the menu bar (top-right corner of your screen).",
  },
  {
    num: 4,
    title: "Connect Assyst Link",
    desc: "Return here and click Browse — Assyst Link will connect automatically.",
  },
];

const PLATFORM_STEPS: Record<string, Array<{ num: number; title: string; desc: string }>> = {
  win32: WIN_STEPS_X64,
  win64: WIN_STEPS_X64,
  win_arm: [
    {
      num: 1,
      title: "Download AssystLinkApp-Setup-arm64.exe",
      desc: "Click the button below to download AssystLinkApp-Setup-arm64.exe. Save it to your Downloads folder or Desktop for easy access.",
    },
    {
      num: 2,
      title: "Install on your computer",
      desc: 'Run AssystLinkApp-Setup-arm64.exe. The app installs silently and starts automatically in the system tray. When prompted, check "Create a Desktop Shortcut" for easy access.',
    },
    {
      num: 3,
      title: "Connect Assyst Link",
      desc: "Return here and click Browse — Assyst Link will connect automatically.",
    },
  ],
  darwin: MAC_STEPS("AssystLinkApp.dmg"),
  darwin_arm: MAC_STEPS("AssystLinkApp-arm64.dmg"),
  linux: [
    {
      num: 1,
      title: "Download AssystLinkApp.AppImage",
      desc: "Click the button below to download AssystLinkApp.AppImage.",
    },
    {
      num: 2,
      title: "Make it executable",
      desc: "Open a terminal and run: chmod +x AssystLinkApp.AppImage",
    },
    {
      num: 3,
      title: "Run the app",
      desc: "Run it: ./AssystLinkApp.AppImage — it will appear in the system tray.",
    },
    {
      num: 4,
      title: "Connect Assyst Link",
      desc: "Return here and click Browse — Assyst Link will connect automatically.",
    },
  ],
};

const WIN_INSTRUCTIONS = {
  text: "If you have already installed Assyst Link, please check your system tray. If the icon is visible, the app is running. If not, start it using one of the following ways:",
  bullets: [
    "Search for Assyst Link in the Windows Taskbar search box and open it, or",
    "Double-click the Assyst Link shortcut on your Desktop.",
  ],
};

const MAC_INSTRUCTIONS = {
  text: "If you have already installed Assyst Link, check your menu bar (top-right corner). If the icon is visible, the app is running. If not, start it using:",
  bullets: [
    "Open Finder → Applications and double-click AssystLinkApp, or",
    "Use Spotlight (⌘ + Space), search for AssystLinkApp and press Enter.",
  ],
};

const PLATFORM_INSTRUCTIONS: Record<string, { text: string; bullets: string[] }> = {
  win32: WIN_INSTRUCTIONS,
  win64: WIN_INSTRUCTIONS,
  win_arm: WIN_INSTRUCTIONS,
  darwin: MAC_INSTRUCTIONS,
  darwin_arm: MAC_INSTRUCTIONS,
  linux: {
    text: "If you have already installed Assyst Link, check your system tray. If the icon is not visible, start the app:",
    bullets: [
      "Run ./AssystLinkApp.AppImage in a terminal, or",
      "Double-click the AppImage file in your file manager.",
    ],
  },
};

const TRAY_LABEL: Record<string, string> = {
  win32: "Windows Taskbar — System Tray",
  win64: "Windows Taskbar — System Tray",
  win_arm: "Windows Taskbar — System Tray",
  darwin: "macOS Menu Bar",
  darwin_arm: "macOS Menu Bar",
  linux: "Linux — System Tray",
};

const AssystLinkModal = ({
  open,
  onClose,
  needsUpdate = false,
  latestVersion,
  platform: platformProp,
  installerName: installerNameProp,
  downloadUrl: downloadUrlProp,
}: AssystLinkModalProps) => {
  const classes = useStyles();
  const toasterId = useId("assyst-link-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const [tcChecked, setTcChecked] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showSteps, setShowSteps] = useState(false);

  const platform = platformProp ?? "win64";
  const installerName = installerNameProp ?? "AssystLinkApp-Setup-x64.exe";
  const steps = PLATFORM_STEPS[platform] ?? PLATFORM_STEPS.win64;
  const instructions = PLATFORM_INSTRUCTIONS[platform] ?? PLATFORM_INSTRUCTIONS.win64;
  const trayLabel = TRAY_LABEL[platform] ?? TRAY_LABEL.win64;

  useEffect(() => {
    if (downloading) {
      let p = 0;
      const interval = setInterval(() => {
        p += 5;
        setProgress(Math.min(p, 90));
        if (p >= 90) clearInterval(interval);
      }, 110);
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [downloading]);

  const handleClose = () => {
    setTcChecked(false);
    setDownloading(false);
    setShowSteps(false);
    setProgress(0);
    onClose();
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await instance.get(
        process.env.REACT_APP_DOWNLOAD_BASEURL + `/document/download/assystlinkapp?platform=${platform}`,
        { responseType: "blob" }
      );

      // 1. Check if the content type is JSON (meaning an error occurred)
      if (res.data.type === "application/json") {
        // Convert the Blob back to text to read the error message
        const text = await res.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || "Installer not available");
      }

      // 2. If it's a real file, proceed with download
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = installerName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (err: any) {
      // 3. Use the actual error message in the Toast
      const errorMessage = err.message || `Could not download ${installerName}.`;

      dispatchToast(
        <Toast>
          <ToastTitle>Download failed</ToastTitle>
          <ToastBody>{errorMessage}</ToastBody>
        </Toast>,
        { intent: "error", timeout: 6000 }
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" />
      <Dialog open={open}>
        <DialogSurface className={classes.surface}>
          {/* ── Header ── */}
          <div
            style={{
              padding: "7px 15px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #cdcdcd",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src={FileLinkImage} alt="Assyst Link Icon" style={{ width: 30, height: 30 }} />
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Assyst Link</h2>
            </div>
            <Button appearance="subtle" icon={<DismissRegular />} onClick={handleClose} style={{ minWidth: 0 }} />
          </div>

          {/* ── Body ── */}
          <div style={{ padding: "10px 12px", overflowY: "auto", flex: 1 }}>

            {/* Update available banner (shown instead of orange warning when update detected) */}
            {needsUpdate ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 6,
                  padding: "7px 10px",
                  marginBottom: 8,
                }}
              >
                <span style={{ color: "#2563eb", fontSize: 17, flexShrink: 0, marginTop: 1 }}>↑</span>
                <div style={{ fontSize: 12, color: "#1e3a5f", lineHeight: 1.55 }}>
                  <strong>
                    A newer version{latestVersion ? ` (${latestVersion})` : ""} of Assyst Link is available.
                  </strong>{" "}
                  Download the update below to get the latest features and improvements.
                </div>
              </div>
            ) : (
              /* Orange warning — shown when app is not installed */
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 6,
                  padding: "7px 10px",
                  marginBottom: 8,
                }}
              >
                <span style={{ color: "#ca8a04", fontSize: 19, flexShrink: 0, marginTop: 1 }}>⚠</span>
                <div style={{ fontSize: 12, color: "#713f12", lineHeight: 1.55 }}>
                  <strong>
                    For the file browser to work, you will need the Assyst Link app to be installed and running.
                  </strong>
                </div>
              </div>
            )}

            {/* Instructions — only shown in install mode */}
            {!needsUpdate && (
              <>
                <p style={{ fontSize: 12, color: "#0f172a", marginBottom: 4, lineHeight: 1.3 }}>
                  {instructions.text}
                </p>
                <ul style={{ fontSize: 12, color: "#0f172a", margin: "5px 0 8px", paddingLeft: 20, lineHeight: 1.3 }}>
                  {instructions.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>

                {/* System tray preview */}
                <div style={{ marginBottom: 2 }}>
                  <div
                    style={{
                      fontSize: 10,
                      color: "rgba(0,0,0,0.8)",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      marginBottom: 4,
                    }}
                  >
                    {trayLabel}
                  </div>
                  <div
                    style={{
                      background: "#1c1c1c",
                      borderRadius: 6,
                      padding: "8px 12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <span style={{ fontSize: 9, color: "white", padding: "0 2px" }}>^</span>
                    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <div
                        style={{
                          width: 25, height: 25,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: "#ffffff", borderRadius: "50%", overflow: "hidden",
                        }}
                      >
                        <img src={FileLinkImage} alt="Assyst Link Icon" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </div>
                    </div>
                    <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="rgba(255,255,255,1)">
                        <path d="M10 4C6.7 4 3.7 5.3 1.5 7.4L3 8.9C4.8 7.1 7.3 6 10 6s5.2 1.1 7 2.9l1.5-1.5C16.3 5.3 13.3 4 10 4zm0 4c-2.2 0-4.2.9-5.6 2.3l1.5 1.5C6.8 10.7 8.3 10 10 10s3.2.7 4.1 1.8l1.5-1.5C14.2 8.9 12.2 8 10 8zm0 4c-1.1 0-2.1.5-2.8 1.2L10 16l2.8-2.8C12.1 12.5 11.1 12 10 12z" />
                      </svg>
                    </div>
                    <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="rgba(255,255,255,1)">
                        <path d="M3 7.5v5h3l4 3.5V4L6 7.5H3zm11 2.5a3 3 0 0 0-2-2.8v5.6A3 3 0 0 0 14 10z" />
                      </svg>
                    </div>
                    <div style={{ fontSize: 10, color: "white", lineHeight: 1.45, textAlign: "right" }}>
                      <div>16:38</div>
                      <div>29/04/2026</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Divider style={{ margin: "8px 0" }} />

            {/* Steps heading */}
            <strong style={{ fontSize: 13, color: "#0f172a" }}>
              {needsUpdate ? "Update Assyst Link App" : "Download Assyst Link App"}
            </strong>

            {/* Toggle steps */}
            <div>
              <Button
                appearance="transparent"
                icon={showSteps ? <ChevronDownRegular /> : <ChevronRightRegular />}
                onClick={() => setShowSteps(!showSteps)}
                style={{ fontSize: 12.5, color: "#1d4ed8", padding: 0, margin: "5px 0 0", minWidth: 0, height: "auto" }}
              >
                View {needsUpdate ? "update" : "installation"} instructions
              </Button>

              {showSteps && (
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                      letterSpacing: "0.08em", color: "#94a3b8", marginBottom: 8,
                    }}
                  >
                    {needsUpdate ? "Update instructions" : "Setup instructions"}
                  </div>
                  {steps.map((step, idx) => (
                    <div key={step.num} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <div
                          style={{
                            width: 18, height: 18, borderRadius: "50%",
                            background: "#205393", color: "#fff",
                            fontSize: 11, fontWeight: 600,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          {step.num}
                        </div>
                        {idx < steps.length - 1 && (
                          <div style={{ width: 1.5, background: "#e2e8f0", flex: 1, marginTop: 4, minHeight: 18 }} />
                        )}
                      </div>
                      <div style={{ paddingBottom: idx < steps.length - 1 ? 8 : 0 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", margin: "0 0 2px" }}>
                          {step.title}
                        </h3>
                        <p style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5, margin: 0 }}>
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div
            style={{
              padding: "10px 12px 12px",
              borderTop: "1px solid #e2e8f0",
              background: "#fff",
              flexShrink: 0,
            }}
          >
            {/* Yellow warning */}
            <div
              style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                background: "#fefce8", border: "1px solid #fde047",
                borderRadius: 6, padding: "7px 10px", marginBottom: 6,
              }}
            >
              <span style={{ color: "#ca8a04", fontSize: 13, flexShrink: 0, marginTop: 1 }}>⚠</span>
              <p style={{ margin: 0, fontSize: 12, color: "#5c300a", lineHeight: 1.5 }}>
                Please do not refresh or close this page until the {needsUpdate ? "update is" : "installation is"} fully complete.
              </p>
            </div>

            {/* Progress bar */}
            {downloading && (
              <div style={{ height: 3, background: "#e2e8f0", borderRadius: 2, margin: "6px 0", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%", background: "#205393", borderRadius: 2,
                    width: `${progress}%`, transition: "width 0.1s linear",
                  }}
                />
              </div>
            )}

            {/* Download button */}
            <Button
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              style={{
                width: "100%",
                background: downloading ? "#2a4a7f" : "#205393",
                border: "none",
                justifyContent: "center",
                margin: "4px 0",
              }}
              disabled={!tcChecked || downloading}
              onClick={handleDownload}
            >
              {downloading ? "Downloading…" : `Download ${installerName}`}
            </Button>

            {/* T&C */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Checkbox checked={tcChecked} onChange={(_, data) => setTcChecked(data.checked === true)} />
              <span style={{ fontSize: 12, color: "#475569" }}>
                By downloading, you agree to the{" "}
                <a href="#" style={{ color: "#3b82f6", textDecoration: "none" }} onClick={(e) => e.preventDefault()}>
                  Terms &amp; Conditions
                </a>
              </span>
            </div>

            {/* WhatsApp contact */}
            <div
              style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 12, color: "#475569",
                marginTop: 8, paddingTop: 8, borderTop: "1px solid #e2e8f0",
                justifyContent: "center",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#25d366" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <span>
                Need help? Reach out to us on{" "}
                <a href="https://wa.me/" target="_blank" rel="noreferrer" style={{ color: "#25d366", fontWeight: 600, textDecoration: "none" }}>
                  WhatsApp
                </a>{" "}
                — we are happy to Assyst!
              </span>
            </div>
          </div>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default AssystLinkModal;
